import { ReactNode } from "react";
import { useAuth } from "@/hooks/use-auth";

interface PageBackgroundProps {
  children: ReactNode;
  className?: string;
  theme?: string;
}

export type ThemeOption = {
  id: string;
  name: string;
  description: string;
};

export const THEME_OPTIONS: ThemeOption[] = [
  { id: "ocean", name: "Cool", description: "Cyan-blue gradient with noise" },
  { id: "warm", name: "Warm", description: "Amber-pink gradient with texture" },
  { id: "forest", name: "Forest", description: "Green-emerald gradient with depth" },
  { id: "sunset", name: "Sunset", description: "Orange-purple gradient with glow" },
  { id: "lavender", name: "Lavender", description: "Purple-pink gradient with softness" },
  { id: "mint", name: "Mint", description: "Mint-green gradient with freshness" },
  { id: "autumn", name: "Autumn", description: "Orange-red gradient with warmth" },
];


function PageBackgroundWarm({ children, className = "" }: PageBackgroundProps) {

  return (
    <div className={`min-h-screen bg-gradient-to-bl from-amber-100 via-pink-100 to-rose-200 relative ${className}`}>
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: `
            radial-gradient(circle at 30% 20%, rgba(255,255,255,0.2) 0%, transparent 50%),
            radial-gradient(circle at 70% 80%, rgba(255,192,203,0.1) 0%, transparent 60%),
          `,
          backgroundSize: "cover, cover, 200px 200px",
          mixBlendMode: "overlay",
        }}
      />
      {children}
    </div>
  );
}

function PageBackgroundOcean({ children, className = "" }: PageBackgroundProps) {

  return (
    <div className={`min-h-screen bg-gradient-to-br from-cyan-100 via-blue-200 to-indigo-300 relative ${className}`}>
      <div
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{
          backgroundImage: `
            radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1) 0%, transparent 70%),
            radial-gradient(circle at 30% 80%, rgba(0,0,255,0.05) 0%, transparent 60%),
          `,
          backgroundSize: "cover, cover, 200px 200px",
          mixBlendMode: "overlay",
        }}
      />
      {children}
    </div>
  );
}

function PageBackgroundForest({ children, className = "" }: PageBackgroundProps) {

  return (
    <div className={`min-h-screen bg-gradient-to-bl from-emerald-100 via-green-200 to-teal-300 relative ${className}`}>
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: `
            radial-gradient(circle at 40% 30%, rgba(255,255,255,0.15) 0%, transparent 60%),
            radial-gradient(circle at 80% 70%, rgba(34,197,94,0.08) 0%, transparent 50%),
          `,
          backgroundSize: "cover, cover, 180px 180px",
          mixBlendMode: "overlay",
        }}
      />
      {children}
    </div>
  );
}

function PageBackgroundSunset({ children, className = "" }: PageBackgroundProps) {

  return (
    <div className={`min-h-screen bg-gradient-to-tr from-orange-200 via-pink-200 to-purple-300 relative ${className}`}>
      <div
        className="absolute inset-0 opacity-18 pointer-events-none"
        style={{
          backgroundImage: `
            radial-gradient(circle at 20% 80%, rgba(255,255,255,0.2) 0%, transparent 55%),
            radial-gradient(circle at 70% 20%, rgba(251,146,60,0.1) 0%, transparent 65%),
          `,
          backgroundSize: "cover, cover, 220px 220px",
          mixBlendMode: "overlay",
        }}
      />
      {children}
    </div>
  );
}

function PageBackgroundLavender({ children, className = "" }: PageBackgroundProps) {

  return (
    <div className={`min-h-screen bg-gradient-to-br from-purple-100 via-pink-100 to-violet-200 relative ${className}`}>
      <div
        className="absolute inset-0 opacity-22 pointer-events-none"
        style={{
          backgroundImage: `
            radial-gradient(circle at 60% 40%, rgba(255,255,255,0.18) 0%, transparent 65%),
            radial-gradient(circle at 30% 70%, rgba(168,85,247,0.06) 0%, transparent 55%),
          `,
          backgroundSize: "cover, cover, 160px 160px",
          mixBlendMode: "overlay",
        }}
      />
      {children}
    </div>
  );
}

function PageBackgroundMint({ children, className = "" }: PageBackgroundProps) {

  return (
    <div className={`min-h-screen bg-gradient-to-tl from-emerald-50 via-green-100 to-cyan-200 relative ${className}`}>
      <div
        className="absolute inset-0 opacity-24 pointer-events-none"
        style={{
          backgroundImage: `
            radial-gradient(circle at 45% 25%, rgba(255,255,255,0.16) 0%, transparent 58%),
            radial-gradient(circle at 75% 85%, rgba(16,185,129,0.07) 0%, transparent 62%),
          `,
          backgroundSize: "cover, cover, 190px 190px",
          mixBlendMode: "overlay",
        }}
      />
      {children}
    </div>
  );
}

function PageBackgroundAutumn({ children, className = "" }: PageBackgroundProps) {

  return (
    <div className={`min-h-screen bg-gradient-to-bl from-orange-100 via-red-200 to-amber-300 relative ${className}`}>
      <div
        className="absolute inset-0 opacity-19 pointer-events-none"
        style={{
          backgroundImage: `
            radial-gradient(circle at 35% 60%, rgba(255,255,255,0.17) 0%, transparent 60%),
            radial-gradient(circle at 85% 25%, rgba(234,88,12,0.09) 0%, transparent 55%),
          `,
          backgroundSize: "cover, cover, 210px 210px",
          mixBlendMode: "overlay",
        }}
      />
      {children}
    </div>
  );
}



export function PageBackground({ children, className = "", theme }: PageBackgroundProps) {
  const { user } = useAuth();
  
  // Get theme from props, user account, or default to "ocean"
  const getTheme = () => {
    if (theme) return theme;
    if (user?.theme) return user.theme;
    return "ocean";
  };

  const currentTheme = getTheme();

  switch (currentTheme) {
    case "warm":
      return <PageBackgroundWarm className={className}>{children}</PageBackgroundWarm>;
    case "forest":
      return <PageBackgroundForest className={className}>{children}</PageBackgroundForest>;
    case "sunset":
      return <PageBackgroundSunset className={className}>{children}</PageBackgroundSunset>;
    case "lavender":
      return <PageBackgroundLavender className={className}>{children}</PageBackgroundLavender>;
    case "mint":
      return <PageBackgroundMint className={className}>{children}</PageBackgroundMint>;
    case "autumn":
      return <PageBackgroundAutumn className={className}>{children}</PageBackgroundAutumn>;
    case "ocean":
    default:
      return <PageBackgroundOcean className={className}>{children}</PageBackgroundOcean>;
  }
}

// Cookie functions removed - theme is now stored only in user account